import { useCallback, useState } from 'react';
import type { PreparedHandle, SaveSource } from './SaveSource';
import { downloadTextFile } from './blob';

export interface StepSaveMessages {
  noData: string;
  nameRequired: string;
  success: string;
  genericError: string;
}

interface UseStepSaveOpts {
  source: SaveSource;
  defaultName: string;
  messages: StepSaveMessages;
}

export function useStepSave({ source, defaultName, messages }: UseStepSaveOpts) {
  const [isModalOpen, setModalOpen] = useState(false);
  const [saveDataName, setSaveDataName] = useState('');
  const [isSaving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const openSaveModal = useCallback(() => {
    if (!source.hasData()) {
      setError(messages.noData);
      return;
    }
    setError(null);
    setSaveDataName(defaultName);
    setModalOpen(true);
  }, [source, defaultName, messages.noData]);

  const onCancel = useCallback(() => setModalOpen(false), []);

  const onConfirm = useCallback(async () => {
    const name = saveDataName.trim();
    if (!name) {
      setError(messages.nameRequired);
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const handle: PreparedHandle | null = await source.prepare(name);
      await source.download(handle, name);
      downloadTextFile(`${name}_info.txt`, source.buildInfoFile(name));
      setSuccess(messages.success);
      setModalOpen(false);
    } catch (e) {
      // Intentionally NOT calling cleanup here — saving never destroys data.
      setError(e instanceof Error ? e.message : messages.genericError);
    } finally {
      setSaving(false);
    }
  }, [saveDataName, source, messages]);

  return {
    openSaveModal,
    canSave: source.hasData(),
    isModalOpen,
    saveDataName,
    setSaveDataName,
    onConfirm,
    onCancel,
    isSaving,
    error,
    success,
  };
}
